/**
 * Page Performance → Pages: every measured page in the scope, server-paged,
 * server-sorted and searchable, with status chips that count the WHOLE
 * scope and filter the table. A row (or its eye) opens Page Detail; the ⓘ
 * beside a P95 opens "How the load times are worked out".
 */
import { useMemo } from 'react'
import { FileSearch } from 'lucide-react'
import { DataTable } from '../table/DataTable'
import { extractApiError } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import { fetchAllPages, usePages } from '../../lib/page-perf/api-client'
import { STATUSES, STATUS_LABEL, normaliseLimits, scopePhrase, usersExactFrom } from '../../lib/page-perf/view-model'
import { buildPageColumns } from './perf-columns'
import { FilterSpinner, ToneDot } from './PerfUi'
import { tintOf, toneOf } from './perf-tones'
import { usePerfTable } from './use-perf-table'

function Chip({ active, onClick, tone, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors ' +
        (active
          ? 'border-[color:var(--color-accent)] text-[color:var(--color-accent-strong)]'
          : 'border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
      }
      style={active ? { backgroundColor: tone ? tintOf(tone) : 'var(--color-accent-bg)' } : undefined}
    >
      {tone ? <ToneDot tone={tone} /> : null}
      {label}
      <b className="tabular-nums">{count == null ? '—' : formatNumber(count)}</b>
    </button>
  )
}

export function PerfPagesTab({ ctx }) {
  const { scope, state, go, slot, companyName, openLoadHelp } = ctx
  const status = state.status
  const table = usePerfTable({
    defaultSort: [{ key: 'p95Ms', dir: 'desc' }],
    resetKey: `${scope.organization}|${scope.period}|${scope.compare}|${status}`,
  })
  const query = usePages(scope, { ...table.params, status })
  const data = query.data
  const limits = useMemo(() => normaliseLimits(data?.limits), [data?.limits])
  const usersExact = usersExactFrom(data?.scope)
  const counts = data?.statusCounts ?? null
  const all = counts ? STATUSES.reduce((s, k) => s + (counts[k] ?? 0), 0) : null

  const columns = useMemo(
    () =>
      buildPageColumns({
        slot,
        limits,
        onOpen: (route) => go({ page: route }),
        onInfo: (row) => openLoadHelp({ ...row, usersExact }),
      }),
    [slot, limits, go, openLoadHelp, usersExact],
  )

  const total = data?.total ?? 0
  const filterChanging = query.isFetching && query.isPlaceholderData
  const where = scopePhrase(companyName, scope.period)

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Chip active={!status} onClick={() => go({ status: '' })} label="All pages" count={all} />
        {STATUSES.map((s) => (
          <Chip
            key={s}
            active={status === s}
            onClick={() => go({ status: status === s ? '' : s })}
            tone={toneOf(s)}
            label={STATUS_LABEL[s]}
            count={counts ? (counts[s] ?? 0) : null}
          />
        ))}
        <FilterSpinner show={filterChanging} />
      </div>
      <p className="m-0 text-xs text-[color:var(--color-ink-muted)]">
        A page&rsquo;s status is the worst of its P95 load time, TTFB, LCP and failed requests against their limits · hover a column
        header for its definition · click a row for the page view · only pages opened in the period are listed.
      </p>

      <DataTable
        tableId="page-perf-pages"
        columns={columns}
        rows={data?.rows ?? []}
        getRowId={(r) => r.route}
        getRowProps={(r) => ({ onClick: () => go({ page: r.route }), className: 'cursor-pointer' })}
        loading={query.isLoading}
        fetching={query.isFetching && !query.isLoading}
        error={query.isError && !data ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        search={table.q}
        onSearchChange={table.setQ}
        searchPlaceholder="Search by route, e.g. /task-monitor"
        defaultSort={table.sort}
        onSortChange={table.setSort}
        page={table.page}
        totalPages={Math.max(1, Math.ceil(total / Math.max(1, table.limit)))}
        total={total}
        limit={table.limit}
        limitOptions={table.limitOptions}
        onPageChange={table.setPage}
        onLimitChange={table.setLimit}
        exportBaseName="page-performance-pages"
        exportTitle="Page Performance — Pages"
        getExportRows={() => fetchAllPages(scope, { ...table.params, status })}
        emptyState={{
          icon: FileSearch,
          title: status
            ? `No ${STATUS_LABEL[status]} pages ${where}`
            : table.q.trim()
              ? `No page matches “${table.q.trim()}” ${where}`
              : `No page opens ${where}`,
          body: status ? 'Pick “All pages” to see every page that was opened.' : 'Pages appear here as soon as someone opens them — within about half a minute.',
        }}
      />
    </div>
  )
}
